/**
 * PATH: apps/ai/src/App.tsx
 * PURPOSE: Main application component for AI Hub.
 *
 * WHY: AI workflows should use the unified layout and settings modal so
 *      access control and preferences are consistent across hubs.
 *
 * FLOW:
 *   ┌──────────────┐    ┌──────────────┐    ┌──────────────┐
 *   │ SSO/Session  │───▶│ Layout Shell │───▶│ AI Routes    │
 *   └──────────────┘    └──────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - @quotech/ui: DashboardLayout, buildSettingsTabs
 *   - @quotech/auth: useAuth, ProtectedRoute, SessionManager
 *   - AI navigation config + pages
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { 
  DashboardLayout, 
  TabBar,
  type User, 
  getHubBreadcrumbs,
  buildSettingsTabs,
  useGlobalSearchBridge,
  useTabNavigation,
  useTabKeyboardShortcuts,
  ErrorBoundary,
  VersionWatcher,
} from '@quotech/ui'
import { useApiClient } from '@quotech/api'
import { 
  useAuth, 
  UnifiedSSOPage, 
  ProtectedRoute, 
  HubGuard,
  useComputedIsAdmin, 
  useAuthStore,
  startSessionMonitoring,
  stopSessionMonitoring,
  getRefreshCookie,
  syncIntegrationStatus,
  useRBAC,
} from '@quotech/auth'
import {
  MODULE_CONFIG,
  NAV_ITEMS,
  SECONDARY_NAV_ITEMS,
  EXTERNAL_LINKS,
  COMMANDS,
  HUB_LINKS,
  QUICK_ADD_ITEMS,
} from './config/navigation'
import { AI_TAB_CONFIG, useAiTabStore } from './config/tabs'

// Section labels - UPPERCASE for consistency with main dashboard
const SECTION_LABELS = {
  main: 'NAVIGATION',
  secondary: 'TOOLS',
  quickAccess: 'QUICK ACCESS',
  hubs: 'HUBS',
}

// Pages
import DashboardPage from './pages/DashboardPage'
import AssistantPage from './pages/AssistantPage'
import ExtractionPage from './pages/ExtractionPage'
import SuggestionsPage from './pages/SuggestionsPage'
import CalculationsPage from './pages/CalculationsPage'
import SimilarityPage from './pages/SimilarityPage'
import SettingsPage from './pages/SettingsPage'
import ReportsPage from './pages/ReportsPage'
import SkillsPage from './pages/SkillsPage'
import OpenClawConceptsPage from './pages/OpenClawConceptsPage'

// Components
import { MobileBottomNav } from './components/MobileBottomNav'

/**
 * Main layout wrapper using @quotech/ui
 */
function AILayout() {
  const { user, logout, organisationId, organisationName } = useAuth()
  const location = useLocation()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const apiClient = useApiClient()

  // Build breadcrumbs using shared utility from hub registry
  const breadcrumbs = getHubBreadcrumbs('ai', location.pathname)
  
  // Global Search Bridge - provides API client with auth for search to work
  const tokenProvider = useMemo(() => ({
    getAccessToken: () => useAuthStore.getState().accessToken,
    getOrgId: () => useAuthStore.getState().selectedOrg?.id || organisationId,
  }), [organisationId])
  
  const { apiClient: searchApiClient } = useGlobalSearchBridge({
    tokenProvider,
    onSettingsOpen: useCallback(() => setSettingsOpen(true), []),
  })

  const computedIsAdmin = useComputedIsAdmin(user as Record<string, any> | null)
  const { accessibleModules, permissions: rbacPermissions, hierarchyLevel } = useRBAC()

  // Map auth user to UI user format - includes ALL admin-related fields
  const uiUser: User & { displayEmail?: string } | null = user ? {
    id: user.id,
    name: user.name || user.full_name || user.email,
    email: user.email,
    // Header-only authority display; login identity stays on `email`.
    displayEmail: user.display_email || user.authority_email || user.email,
    organisation: user.organisation_name,
    organisationId: user.organisation_id,
    role: user.role,
    isAdmin: computedIsAdmin,
    admin: (user as Record<string, unknown>).admin as boolean | undefined,
    is_admin: (user as Record<string, unknown>).is_admin as boolean | undefined,
    is_staff: (user as Record<string, unknown>).is_staff as boolean | undefined,
    is_superuser: (user as Record<string, unknown>).is_superuser as boolean | undefined,
  } : null

  // Handle logout with redirect to master login
  const handleLogout = () => {
    logout()
    window.location.href = 'https://dashboard.quotech.ai/login'
  }

  // Build settings tabs using universal helper
  const settingsTabs = useMemo(() => buildSettingsTabs({
    user: user ? {
      id: user.id,
      first_name: user.first_name || user.name?.split(' ')[0],
      last_name: user.last_name || user.name?.split(' ').slice(1).join(' '),
      email: user.email,
      organisation_name: user.organisation_name || organisationName,
      role: user.role,
      isAdmin: computedIsAdmin,
      permissions: rbacPermissions, hierarchy_level: hierarchyLevel,
    } : null,
    hubId: 'ai',
    hubName: 'AI Hub',
    apiClient,
    organisationId: organisationId || user?.organisation_id,
    profileData: {
      first_name: user?.first_name || user?.name?.split(' ')[0],
      last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' '),
      email: user?.email,
      organisation_name: user?.organisation_name || organisationName,
    },
    organisationData: {
      id: organisationId || user?.organisation_id || '',
      name: organisationName || user?.organisation_name || 'Organisation',
    },
    integrations: [], // AI Hub doesn't have direct integrations
    shortcuts: [
      { label: 'Send message', keys: ['⌘', 'Enter'], category: 'AI Hub' },
      { label: 'Clear input', keys: ['Esc'], category: 'AI Hub' },
    ],
  }), [user, organisationId, organisationName, apiClient, computedIsAdmin, rbacPermissions, hierarchyLevel])
  
  // Update SECONDARY_NAV_ITEMS to use action for settings
  const secondaryNavWithSettingsAction = useMemo(() => 
    SECONDARY_NAV_ITEMS.map(item => 
      item.id === 'settings' 
        ? { ...item, href: undefined, action: () => setSettingsOpen(true) }
        : item
    ), []
  )

  // ── Tab navigation ─────────────────────────────────────
  const tabNav = useTabNavigation(AI_TAB_CONFIG)
  useTabKeyboardShortcuts({ useStore: useAiTabStore, ...tabNav })

  return (
    <>
      <DashboardLayout
        navItems={NAV_ITEMS}
        secondaryNavItems={secondaryNavWithSettingsAction}
        externalLinks={EXTERNAL_LINKS}
        module={MODULE_CONFIG}
        currentHub="ai"
        accessibleModules={accessibleModules}
        user={uiUser}
        onLogout={handleLogout}
        breadcrumbs={breadcrumbs}
        showGlobalSearch={true}
        globalSearchApiClient={searchApiClient}
        showCommandPalette
        commands={COMMANDS}
        hubLinks={HUB_LINKS}
        showOrgSwitcher
        showNotifications
        showHubSwitcher
        showQuickAdd
        quickAddItems={QUICK_ADD_ITEMS}
        sidebarSectionLabels={SECTION_LABELS}
        noContentPadding
        showSettingsModal
        settingsTabs={settingsTabs}
        settingsModalOpen={settingsOpen}
        onCloseSettings={() => setSettingsOpen(false)}
        onOpenSettings={() => setSettingsOpen(true)}
        headerTabsSlot={
          <TabBar
            variant="inline"
            tabs={tabNav.tabItems}
            activeTabId={tabNav.activeTabId}
            onTabClick={tabNav.handleTabClick}
            onTabClose={tabNav.handleTabClose}
            onNewTab={tabNav.handleNewTab}
            onTabPin={tabNav.handleTabPin}
            onCloseOthers={tabNav.handleCloseOthers}
            onDuplicateTab={tabNav.handleDuplicateTab}
            accentColor="bg-teal-500"
          />
        }
      />
      {/* Mobile Bottom Navigation - shows on small screens */}
      <MobileBottomNav />
    </>
  )
}

/**
 * Main App with unified SSO and session management
 */
function App() {
  // Initialize unified session monitoring (same pattern as QDrive, CRM, QA)
  useEffect(() => {
    const manager = startSessionMonitoring({
      onSessionExpired: (reason) => {
      },
      onTokenRefreshed: (newToken) => {
        
        // CRITICAL: Update Zustand store with the new token from SessionManager
        const refreshToken = getRefreshCookie()
        useAuthStore.getState().updateTokens(newToken, refreshToken || undefined)
        
        syncIntegrationStatus(newToken).catch(err => {
          console.warn('[AI Hub] Failed to sync integration status:', err)
        })
      },
    })

    // Initial integration status sync
    syncIntegrationStatus().catch(err => {
      console.warn('[AI Hub] Failed to sync integration status:', err)
    })

    return () => {
      stopSessionMonitoring()
    }
  }, [])

  return (
    <ErrorBoundary>
    {/* Auto-update notifier — see qf!2578 for context (30s poll, 10-min idle auto-reload). */}
    <VersionWatcher pollIntervalMs={30_000} autoReloadAfterMs={null} />
    <Routes>
      {/* Unified SSO Page - processes token from URL */}
      <Route
        path="/sso"
        element={
          <UnifiedSSOPage
            defaultRedirectPath="/chat"
            loadingComponent={
              <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                  <div className="w-10 h-10 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Signing you in to AI Hub...</p>
                </div>
              </div>
            }
          />
        }
      />

      {/* Protected routes with shared layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute redirectTo="https://dashboard.quotech.ai/login">
            <HubGuard hub="ai"><AILayout /></HubGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/chat" replace />} />
        <Route path="chat" element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="skills" element={<SkillsPage />} />
        <Route path="assistant" element={<Navigate to="/chat" replace />} />
        <Route path="extraction" element={<ExtractionPage />} />
        <Route path="suggestions" element={<SuggestionsPage />} />
        <Route path="calculations" element={<CalculationsPage />} />
        <Route path="similarity" element={<SimilarityPage />} />
        <Route path="insights" element={<SuggestionsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="anomalies" element={<Navigate to="/dashboard" replace />} />
        <Route path="prompts" element={<Navigate to="/settings" replace />} />
        <Route path="training" element={<Navigate to="/settings" replace />} />
        <Route path="usage" element={<Navigate to="/dashboard" replace />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="concepts" element={<OpenClawConceptsPage />} />
        <Route path="concepts/openclaw" element={<OpenClawConceptsPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/chat" replace />} />
    </Routes>
    </ErrorBoundary>
  )
}

export default App
